import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { AuthImage } from "@/components/AuthImage";
import { PlantDialog } from "@/components/PlantDialog";
import { LogEntryDialog } from "@/components/LogEntryDialog";
import { AiPlantDoctor } from "@/components/AiPlantDoctor";
import { PlantGallery } from "@/components/PhotoGallery";
import { MilestoneBadges } from "@/components/MilestoneBadges";
import { HarvestSection } from "@/components/HarvestSection";
import { stageMeta, formatDate, relativeDays } from "@/lib/plant";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ArrowLeft, Droplets, Pencil, Trash2, Plus, MapPin, Calendar, Ruler, NotebookPen } from "lucide-react";
import { toast } from "sonner";

export default function SproutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plant, setPlant] = useState(null);
  const [logs, setLogs] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([api.get(`/plants/${id}`), api.get(`/plants/${id}/logs`)]);
      setPlant(p.data);
      setLogs(l.data);
    } catch {
      toast.error("Sprout not found");
      navigate("/sprouts");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const water = async () => {
    await api.post(`/plants/${id}/water`);
    toast.success("Watered 💧");
    load();
  };

  const remove = async () => {
    await api.delete(`/plants/${id}`);
    toast.success("Sprout removed");
    navigate("/sprouts");
  };

  const removeLog = async (logId) => {
    await api.delete(`/logs/${logId}`);
    load();
  };

  if (!plant) {
    return (
      <Layout>
        <div className="h-64 rounded-2xl bg-card border border-border animate-pulse" />
      </Layout>
    );
  }

  const meta = stageMeta(plant.growth_stage);
  const StageIcon = meta.icon;
  const heightData = logs.filter((l) => l.height_cm != null).map((l) => ({ date: formatDate(l.date), height: l.height_cm }));
  const hasPhoto = Boolean(plant.photo_path) || logs.some((l) => (l.photo_paths && l.photo_paths.length) || l.photo_path);

  return (
    <Layout>
      <button onClick={() => navigate("/sprouts")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back to sprouts
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: hero + info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-sm">
            <AuthImage path={plant.photo_path} className="w-full h-64 object-cover" fallbackClassName="w-full h-64" />
          </div>
          <div className="bg-card rounded-2xl border border-border p-5 shadow-sm space-y-4">
            <div>
              <div className="flex items-start justify-between gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="plant-name">{plant.name}</h1>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${meta.color}`}>
                  <StageIcon className="h-3.5 w-3.5" /> {plant.growth_stage}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{[plant.species, plant.variety].filter(Boolean).join(" · ") || "Unspecified species"}</p>
            </div>

            <div className="space-y-2.5 text-sm">
              {plant.location && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" /> {plant.location}
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" /> Planted {formatDate(plant.planted_date)}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Droplets className="h-4 w-4" /> Water every {plant.watering_interval_days}d · last {plant.last_watered ? relativeDays(plant.last_watered) : "never"}
              </div>
            </div>

            {plant.notes && <p className="text-sm bg-secondary/60 rounded-xl p-3 text-secondary-foreground">{plant.notes}</p>}

            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={water} className="flex-1 bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="detail-water-button">
                <Droplets className="h-4 w-4 mr-1.5" /> Water
              </Button>
              <Button variant="secondary" onClick={() => setEditOpen(true)} className="rounded-xl" data-testid="detail-edit-button">
                <Pencil className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="secondary" className="rounded-xl text-destructive hover:text-destructive" data-testid="detail-delete-button">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {plant.name}?</AlertDialogTitle>
                    <AlertDialogDescription>This deletes the sprout and all its growth logs. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="delete-confirm">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>

        {/* Right: chart + timeline */}
        <div className="lg:col-span-2 space-y-6">
          <MilestoneBadges plantId={plant.id} refreshKey={logs.length + (plant.growth_stage || "")} />

          <AiPlantDoctor plant={plant} hasPhoto={hasPhoto} />

          <PlantGallery plantId={plant.id} refreshKey={logs.length} />

          <HarvestSection plant={plant} onChanged={load} />

          {heightData.length > 1 && (
            <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm">
              <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <Ruler className="h-5 w-5 text-emerald-600" /> Height Over Time
              </h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={heightData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} unit="cm" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                  <Line type="monotone" dataKey="height" stroke="#15803D" strokeWidth={2.5} dot={{ r: 4, fill: "#15803D" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <NotebookPen className="h-5 w-5 text-emerald-600" /> Growth Timeline
              </h2>
              <Button onClick={() => setLogOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-log-button">
                <Plus className="h-4 w-4 mr-1.5" /> Log entry
              </Button>
            </div>

            {logs.length === 0 ? (
              <div className="text-center py-10">
                <NotebookPen className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                <p className="text-muted-foreground">No entries yet. Log this sprout's first milestone!</p>
              </div>
            ) : (
              <div className="relative pl-6" data-testid="growth-timeline">
                <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
                <div className="space-y-6">
                  {[...logs].reverse().map((log) => {
                    const lm = stageMeta(log.growth_stage);
                    const LIcon = lm.icon;
                    return (
                      <div key={log.id} className="relative group" data-testid="timeline-entry">
                        <div className="absolute -left-6 top-1 h-4 w-4 rounded-full border-2 border-card" style={{ background: lm.ring }} />
                        <div className="flex flex-col sm:flex-row gap-3">
                          {(() => {
                            const photos = (log.photo_paths && log.photo_paths.length ? log.photo_paths : log.photo_path ? [log.photo_path] : []);
                            if (!photos.length) return null;
                            return (
                              <div className="flex gap-2 shrink-0 flex-wrap">
                                {photos.map((p) => (
                                  <AuthImage key={p} path={p} className="w-24 h-24 rounded-xl object-cover border border-border" fallbackClassName="w-24 h-24 rounded-xl" />
                                ))}
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${lm.color}`}>
                                <LIcon className="h-3 w-3" /> {log.growth_stage || "Note"}
                              </span>
                              {log.height_cm != null && (
                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                  <Ruler className="h-3 w-3" /> {log.height_cm} cm
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">· {formatDate(log.date)}</span>
                              <button onClick={() => removeLog(log.id)} className="ml-auto opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive" data-testid={`delete-log-${log.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {log.note && <p className="text-sm mt-1.5 text-foreground/90">{log.note}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PlantDialog open={editOpen} onOpenChange={setEditOpen} onSaved={load} plant={plant} />
      <LogEntryDialog open={logOpen} onOpenChange={setLogOpen} onSaved={load} plant={plant} />
    </Layout>
  );
}
